import * as THREE from 'three'
import * as CANNON from 'cannon-es'
import Experience from '../Experience.js'

export default class Floor {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.worldPhysics = this.experience.worldPhysics.instance
        
        // Vibrant Nintendo racing colors
        this.nintendoColors = {
            trackColor: 0x171b20,         // Darker track
            roadColor: 0x24282e,          // Main road
            roadStripeColor: 0xd8d9d5,    // Slightly aged road paint
            grassColor: 0x263c30,         // Vibrant green grass
            grassColorAlt: 0x2b4435,      // Alternate grass for pattern
            fenceColor: 0x30363a,         // Weathered guard rail
            fenceAccent: 0xc2c5c2,        // Reflective safety accents
            boostPadColor: 0x168fba,      // Racing cyan boost pads
            boostArrowColor: 0xb7f2ff     // Luminous boost arrows
        }
        
        // Store meshes for updates
        this.fenceMeshes = []
        this.grassMeshes = []
        this.boostPads = []
        
        // Build the track
        this.createTrack()
        this.setPhysics()
        this.addBoostPads()
        this.addNintendoFences()
        this.addTrees()
        this.addTracksideDetails()
    }

    createTrack() {
        // Create a proper race track layout
        
        // Main ground (grass)
        const groundGeometry = new THREE.PlaneGeometry(200, 200)
        const groundMaterial = new THREE.MeshStandardMaterial({
            color: this.nintendoColors.grassColor, roughness: 1.0
        })
        this.groundMesh = new THREE.Mesh(groundGeometry, groundMaterial)
        this.groundMesh.rotation.x = -Math.PI * 0.5
        this.groundMesh.receiveShadow = true
        this.scene.add(this.groundMesh)
        this.grassMeshes.push(this.groundMesh)
        
        // Create checkered grass pattern
        // Natural continuous grass: remove the old checkerboard pattern.
        
        // Create the main track (oval-ish shape using multiple pieces)
        this.createRoadSurface()
        
        // Add road markings only on the road
        this.addRoadMarkings()
    }
    
    addGrassPattern() {
        // Add alternating grass squares for Mario Kart-like look
        const squareSize = 20
        const grassAltMaterial = new THREE.MeshStandardMaterial({
            color: this.nintendoColors.grassColorAlt, roughness: 1.0
        })
        
        for (let x = -80; x < 80; x += squareSize * 2) {
            for (let z = -80; z < 80; z += squareSize * 2) {
                // Create checkered pattern
                const offset = ((x / squareSize) % 2 === 0) ? 0 : squareSize
                
                const square = new THREE.Mesh(
                    new THREE.PlaneGeometry(squareSize, squareSize),
                    grassAltMaterial
                )
                square.position.set(x + offset, 0.005, z)
                square.rotation.x = -Math.PI / 2
                square.receiveShadow = true
                this.scene.add(square)
            }
        }
    }
    
    createRoadSurface() {
        // Procedural asphalt: fine aggregate, faint repairs and subtle rubber streaks.
        // Generated locally so the game does not need external texture files.
        const asphaltTexture = this.createAsphaltTexture()
        const roadMaterial = new THREE.MeshStandardMaterial({
            color: 0xffffff, map: asphaltTexture, roughness: 0.66, metalness: 0.12
        })
        this.roadMaterial = roadMaterial
        
        // Main circular track
        const trackWidth = 18
        const trackRadius = 50
        
        // Create track using a ring shape
        const trackShape = new THREE.Shape()
        const outerRadius = trackRadius + trackWidth / 2
        const innerRadius = trackRadius - trackWidth / 2
        
        // Outer circle
        trackShape.absarc(0, 0, outerRadius, 0, Math.PI * 2, false)
        
        // Inner circle (hole)
        const hole = new THREE.Path()
        hole.absarc(0, 0, innerRadius, 0, Math.PI * 2, true)
        trackShape.holes.push(hole)
        
        const trackGeometry = new THREE.ShapeGeometry(trackShape, 64)
        const trackMesh = new THREE.Mesh(trackGeometry, roadMaterial)
        trackMesh.rotation.x = -Math.PI / 2
        trackMesh.position.y = 0.02
        trackMesh.receiveShadow = true
        this.scene.add(trackMesh)
        
        // Add starting area (straight section)
        const startAreaGeo = new THREE.PlaneGeometry(20, 25)
        const startArea = new THREE.Mesh(startAreaGeo, roadMaterial)
        startArea.rotation.x = -Math.PI / 2
        startArea.position.set(50, 0.02, 0)
        startArea.receiveShadow = true
        this.scene.add(startArea)
        
        // Starting line
        this.addStartingLine()
    }
    
    createAsphaltTexture() {
        const size = 512
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#252a30'
        ctx.fillRect(0, 0, size, size)

        // Broad, barely visible resurfacing patches.
        for (let i = 0; i < 22; i++) {
            const x = Math.random() * size
            const y = Math.random() * size
            const w = 25 + Math.random() * 115
            const h = 12 + Math.random() * 55
            ctx.fillStyle = i % 2 ? 'rgba(100,112,121,0.055)' : 'rgba(0,0,0,0.09)'
            ctx.fillRect(x, y, w, h)
        }
        // Fine aggregate speckles; restrained contrast avoids visual noise on mobile.
        for (let i = 0; i < 18000; i++) {
            const shade = Math.floor(90 + Math.random() * 100)
            const alpha = 0.025 + Math.random() * 0.09
            ctx.fillStyle = `rgba(${shade},${shade + 3},${shade + 6},${alpha})`
            const r = Math.random() > 0.92 ? 1.5 : 0.6
            ctx.fillRect(Math.random() * size, Math.random() * size, r, r)
        }
        // Subtle long rubber streaks in the driving direction.
        for (let i = 0; i < 14; i++) {
            const x = Math.random() * size
            ctx.strokeStyle = `rgba(8,10,12,${0.07 + Math.random() * 0.09})`
            ctx.lineWidth = 1 + Math.random() * 3
            ctx.beginPath()
            ctx.moveTo(x, Math.random() * 80)
            ctx.lineTo(x + (Math.random() - 0.5) * 18, size - Math.random() * 80)
            ctx.stroke()
        }
        const texture = new THREE.CanvasTexture(canvas)
        texture.wrapS = THREE.RepeatWrapping
        texture.wrapT = THREE.RepeatWrapping
        texture.repeat.set(2, 2)
        texture.anisotropy = 4
        texture.encoding = THREE.sRGBEncoding
        return texture
    }

    addTracksideDetails() {
        // Low-poly LED pylons add scale and a modern racing-event atmosphere
        // without creating dozens of expensive dynamic lights.
        const poleMaterial = new THREE.MeshStandardMaterial({ color: 0x202a34, metalness: 0.72, roughness: 0.32 })
        const cyanMaterial = new THREE.MeshStandardMaterial({
            color: 0x42dfff, emissive: 0x12a9ff, emissiveIntensity: 1.7,
            metalness: 0.2, roughness: 0.24
        })
        const warmMaterial = new THREE.MeshStandardMaterial({
            color: 0xffd7a0, emissive: 0xff9d4d, emissiveIntensity: 0.85,
            metalness: 0.1, roughness: 0.3
        })
        const poleGeo = new THREE.CylinderGeometry(0.22, 0.32, 6.5, 8)
        const lampGeo = new THREE.BoxGeometry(1.7, 0.22, 0.42)
        const radius = 72
        for (let i = 0; i < 12; i++) {
            const a = (i / 12) * Math.PI * 2 + Math.PI / 12
            const x = Math.cos(a) * radius
            const z = Math.sin(a) * radius
            const pole = new THREE.Mesh(poleGeo, poleMaterial)
            pole.position.set(x, 3.25, z)
            pole.castShadow = true
            this.scene.add(pole)
            const lamp = new THREE.Mesh(lampGeo, i % 3 === 0 ? warmMaterial : cyanMaterial)
            lamp.position.set(x - Math.cos(a) * 0.6, 6.35, z - Math.sin(a) * 0.6)
            lamp.rotation.y = -a
            this.scene.add(lamp)
            const base = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.58, 0.28, 8), poleMaterial)
            base.position.set(x, 0.14, z)
            this.scene.add(base)
        }

        // Start/finish gantry with a custom Carpano Racing banner.
        const gantry = new THREE.Group()
        const frameMat = new THREE.MeshStandardMaterial({ color: 0x151c25, metalness: 0.8, roughness: 0.28 })
        const beam = new THREE.Mesh(new THREE.BoxGeometry(25, 0.7, 0.8), frameMat)
        beam.position.y = 7.3
        gantry.add(beam)
        for (const x of [-12, 12]) {
            const leg = new THREE.Mesh(new THREE.BoxGeometry(0.65, 7.2, 0.65), frameMat)
            leg.position.set(x, 3.6, 0)
            gantry.add(leg)
            const accent = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.12, 0.72), cyanMaterial)
            accent.position.set(x, 6.9, 0)
            gantry.add(accent)
        }
        const signCanvas = document.createElement('canvas')
        signCanvas.width = 1024
        signCanvas.height = 256
        const signCtx = signCanvas.getContext('2d')
        const gradient = signCtx.createLinearGradient(0, 0, 1024, 256)
        gradient.addColorStop(0, '#071521')
        gradient.addColorStop(0.5, '#12354a')
        gradient.addColorStop(1, '#071521')
        signCtx.fillStyle = gradient
        signCtx.fillRect(0, 0, 1024, 256)
        signCtx.fillStyle = '#31d7ff'
        signCtx.fillRect(0, 0, 1024, 12)
        signCtx.fillRect(0, 244, 1024, 12)
        signCtx.font = '900 94px Arial, sans-serif'
        signCtx.textAlign = 'center'
        signCtx.textBaseline = 'middle'
        signCtx.fillStyle = '#ffffff'
        signCtx.fillText('CARPANO RACING', 512, 128)
        const signTexture = new THREE.CanvasTexture(signCanvas)
        signTexture.encoding = THREE.sRGBEncoding
        const sign = new THREE.Mesh(new THREE.PlaneGeometry(18, 4.5), new THREE.MeshBasicMaterial({ map: signTexture, side: THREE.DoubleSide }))
        sign.position.set(0, 5.35, 0.44)
        gantry.add(sign)
        gantry.position.set(50, 0, 10)
        this.scene.add(gantry)

        // A few subtle wet patches break up the asphalt and catch the scene lighting.
        // They are flat meshes (no extra lights/reflections) to keep mobile cost low.
        const puddleMaterial = new THREE.MeshStandardMaterial({
            color: 0x637783, metalness: 0.48, roughness: 0.19,
            transparent: true, opacity: 0.22, depthWrite: false,
            polygonOffset: true, polygonOffsetFactor: -1
        })
        for (let i = 0; i < 10; i++) {
            const a = (i / 10) * Math.PI * 2 + 0.17
            const r = 42 + (i % 4) * 4.2
            const puddle = new THREE.Mesh(new THREE.CircleGeometry(1, 18), puddleMaterial)
            puddle.rotation.x = -Math.PI / 2
            puddle.rotation.z = (i * 0.71) % Math.PI
            puddle.position.set(Math.cos(a) * r, 0.031, Math.sin(a) * r)
            puddle.scale.set(1.2 + (i % 3) * 0.45, 0.55 + (i % 2) * 0.25, 1)
            this.scene.add(puddle)
        }

        // Small reflective marker blocks guide the eye around the outer curve.
        const markerGeo = new THREE.BoxGeometry(1.1, 0.32, 0.45)
        const markerMaterials = [
            new THREE.MeshStandardMaterial({ color: 0xeaf8ff, emissive: 0x6bbdff, emissiveIntensity: 0.45, roughness: 0.35 }),
            new THREE.MeshStandardMaterial({ color: 0xf64b54, emissive: 0x8b101c, emissiveIntensity: 0.3, roughness: 0.42 })
        ]
        for (let i = 0; i < 64; i++) {
            const a = i / 64 * Math.PI * 2
            const r = 63.5
            const marker = new THREE.Mesh(markerGeo, markerMaterials[Math.floor(i / 4) % 2])
            marker.position.set(Math.cos(a) * r, 0.22, Math.sin(a) * r)
            marker.rotation.y = -a
            this.scene.add(marker)
        }
    }

    addStartingLine() {
        // Checkered starting line
        const squareSize = 1.5
        const lineWidth = 18
        const numSquares = Math.floor(lineWidth / squareSize)
        
        const whiteMat = new THREE.MeshStandardMaterial({ color: 0xFFFFFF })
        const blackMat = new THREE.MeshStandardMaterial({ color: 0x111111 })
        
        for (let i = 0; i < numSquares; i++) {
            for (let row = 0; row < 2; row++) {
                const isWhite = (i + row) % 2 === 0
                const square = new THREE.Mesh(
                    new THREE.PlaneGeometry(squareSize, squareSize),
                    isWhite ? whiteMat : blackMat
                )
                square.position.set(
                    50 + (i - numSquares / 2) * squareSize + squareSize / 2,
                    0.03,
                    10 + row * squareSize
                )
                square.rotation.x = -Math.PI / 2
                this.scene.add(square)
            }
        }
    }

    addRoadMarkings() {
        const lineMaterial = new THREE.MeshBasicMaterial({
            color: this.nintendoColors.roadStripeColor
        })
        
        // Center dashed line around the circular track
        const trackRadius = 50
        const numDashes = 40
        
        for (let i = 0; i < numDashes; i++) {
            const angle = (i / numDashes) * Math.PI * 2
            const x = Math.cos(angle) * trackRadius
            const z = Math.sin(angle) * trackRadius
            
            const dashGeometry = new THREE.PlaneGeometry(0.4, 3)
            const dash = new THREE.Mesh(dashGeometry, lineMaterial)
            dash.position.set(x, 0.025, z)
            dash.rotation.x = -Math.PI / 2
            dash.rotation.z = -angle + Math.PI / 2
            this.scene.add(dash)
        }
        
        // Edge lines (inner and outer)
        const innerRadius = 50 - 8
        const outerRadius = 50 + 8
        
        // Inner edge - solid line
        const innerLine = this.createCircleLine(innerRadius, 0xFFFFFF, 0.3)
        this.scene.add(innerLine)
        
        // Outer edge - solid line
        const outerLine = this.createCircleLine(outerRadius, 0xFFFFFF, 0.3)
        this.scene.add(outerLine)
        
        // Red/white curbs on edges
        this.addCurbs(innerRadius - 0.5, 48)
        this.addCurbs(outerRadius + 0.5, 60)
    }
    
    createCircleLine(radius, color, width) {
        const points = []
        for (let i = 0; i <= 64; i++) {
            const angle = (i / 64) * Math.PI * 2
            points.push(new THREE.Vector3(
                Math.cos(angle) * radius,
                0.025,
                Math.sin(angle) * radius
            ))
        }
        
        const geometry = new THREE.BufferGeometry().setFromPoints(points)
        const material = new THREE.LineBasicMaterial({ color, linewidth: 2 })
        return new THREE.Line(geometry, material)
    }
    
    addCurbs(radius, numCurbs) {
        const redMat = new THREE.MeshStandardMaterial({ color: 0xFF0000 })
        const whiteMat = new THREE.MeshStandardMaterial({ color: 0xFFFFFF })
        
        for (let i = 0; i < numCurbs; i++) {
            const angle = (i / numCurbs) * Math.PI * 2
            const x = Math.cos(angle) * radius
            const z = Math.sin(angle) * radius
            
            const curb = new THREE.Mesh(
                new THREE.BoxGeometry(1.5, 0.1, 0.8),
                i % 2 === 0 ? redMat : whiteMat
            )
            curb.position.set(x, 0.05, z)
            curb.rotation.y = -angle + Math.PI / 2
            this.scene.add(curb)
        }
    }

    addBoostPads() {
        // Add speed boost pads around the track
        const boostPositions = [
            { x: 50, z: -50, angle: Math.PI },      // Bottom of track
            { x: -50, z: 0, angle: Math.PI / 2 },   // Left side
            { x: 0, z: 50, angle: 0 },              // Top of track
            { x: 50, z: 20, angle: -Math.PI / 2 }   // Near start
        ]
        
        boostPositions.forEach(pos => {
            this.createBoostPad(pos.x, pos.z, pos.angle)
        })
    }
    
    createBoostPad(x, z, angle) {
        const padGroup = new THREE.Group()
        
        // Main pad
        const padGeometry = new THREE.PlaneGeometry(6, 3)
        const padMaterial = new THREE.MeshStandardMaterial({
            color: this.nintendoColors.boostPadColor,
            emissive: this.nintendoColors.boostPadColor,
            emissiveIntensity: 0.3
        })
        const pad = new THREE.Mesh(padGeometry, padMaterial)
        pad.rotation.x = -Math.PI / 2
        padGroup.add(pad)
        
        // Arrow shapes
        const arrowMaterial = new THREE.MeshStandardMaterial({
            color: this.nintendoColors.boostArrowColor,
            emissive: this.nintendoColors.boostArrowColor,
            emissiveIntensity: 0.5
        })
        
        for (let i = 0; i < 2; i++) {
            const arrowShape = new THREE.Shape()
            arrowShape.moveTo(0, 0.8)
            arrowShape.lineTo(0.5, 0)
            arrowShape.lineTo(0.2, 0)
            arrowShape.lineTo(0.2, -0.6)
            arrowShape.lineTo(-0.2, -0.6)
            arrowShape.lineTo(-0.2, 0)
            arrowShape.lineTo(-0.5, 0)
            arrowShape.lineTo(0, 0.8)
            
            const arrowGeo = new THREE.ShapeGeometry(arrowShape)
            const arrow = new THREE.Mesh(arrowGeo, arrowMaterial)
            arrow.rotation.x = -Math.PI / 2
            arrow.position.set(-1 + i * 2, 0.01, 0)
            arrow.scale.set(1.2, 1.2, 1)
            padGroup.add(arrow)
        }
        
        padGroup.position.set(x, 0.03, z)
        padGroup.rotation.y = angle
        this.scene.add(padGroup)
        
        // Store for game logic
        this.boostPads.push({
            mesh: padGroup,
            position: new THREE.Vector3(x, 0, z),
            radius: 4
        })
    }

    addNintendoFences() {
        // Create racing fences around the track perimeter
        const fenceHeight = 1.5
        const outerRadius = 75
        const numPosts = 40
        
        for (let i = 0; i < numPosts; i++) {
            const angle = (i / numPosts) * Math.PI * 2
            const x = Math.cos(angle) * outerRadius
            const z = Math.sin(angle) * outerRadius
            
            this.createFencePost(x, z, angle)
        }
        
        // Add invisible physics walls
        this.addInvisibleWalls()
    }
    
    createFencePost(x, z, angle) {
        const postGroup = new THREE.Group()
        
        // Main post
        const postGeometry = new THREE.BoxGeometry(0.3, 1.5, 0.3)
        const postMaterial = new THREE.MeshStandardMaterial({
            color: this.nintendoColors.fenceColor
        })
        const post = new THREE.Mesh(postGeometry, postMaterial)
        post.position.y = 0.75
        post.castShadow = true
        postGroup.add(post)
        
        // White cap
        const capGeometry = new THREE.BoxGeometry(0.4, 0.15, 0.4)
        const capMaterial = new THREE.MeshStandardMaterial({
            color: this.nintendoColors.fenceAccent
        })
        const cap = new THREE.Mesh(capGeometry, capMaterial)
        cap.position.y = 1.55
        postGroup.add(cap)
        
        postGroup.position.set(x, 0, z)
        this.scene.add(postGroup)
        this.fenceMeshes.push(postGroup)
        
        // Physics for fence post
        const postBody = new CANNON.Body({
            type: CANNON.Body.STATIC,
            shape: new CANNON.Box(new CANNON.Vec3(0.3, 1, 0.3)),
            position: new CANNON.Vec3(x, 1, z)
        })
        this.worldPhysics.addBody(postBody)
    }
    
    addTrees() {
        // Add trees outside the track
        const treePositions = []
        const outerRadius = 85
        
        for (let i = 0; i < 20; i++) {
            const angle = (i / 20) * Math.PI * 2 + Math.random() * 0.2
            const radius = outerRadius + 5 + Math.random() * 10
            treePositions.push({
                x: Math.cos(angle) * radius,
                z: Math.sin(angle) * radius
            })
        }
        
        treePositions.forEach(pos => {
            this.createNintendoTree(pos.x, pos.z)
        })
    }
    
    createNintendoTree(x, z) {
        const treeGroup = new THREE.Group()
        
        // Trunk
        const trunkGeometry = new THREE.CylinderGeometry(0.3, 0.5, 2, 8)
        const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 })
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial)
        trunk.position.y = 1
        trunk.castShadow = true
        treeGroup.add(trunk)
        
        // Foliage (3 stacked cones)
        const foliageMaterial = new THREE.MeshStandardMaterial({ color: 0x228B22 })
        
        const cone1 = new THREE.Mesh(new THREE.ConeGeometry(2, 2.5, 8), foliageMaterial)
        cone1.position.y = 3
        cone1.castShadow = true
        treeGroup.add(cone1)
        
        const cone2 = new THREE.Mesh(new THREE.ConeGeometry(1.6, 2, 8), foliageMaterial)
        cone2.position.y = 4.5
        cone2.castShadow = true
        treeGroup.add(cone2)
        
        const cone3 = new THREE.Mesh(new THREE.ConeGeometry(1, 1.5, 8), foliageMaterial)
        cone3.position.y = 5.8
        cone3.castShadow = true
        treeGroup.add(cone3)
        
        treeGroup.position.set(x, 0, z)
        treeGroup.rotation.y = Math.random() * Math.PI * 2
        
        // Slight random scale for variety
        const scale = 0.8 + Math.random() * 0.6
        treeGroup.scale.set(scale, scale, scale)
        
        this.scene.add(treeGroup)

        // Physics collider (static trunk)
        // Cannon cylinder is oriented along X by default; rotate it to align with Y.
        const trunkRadiusTop = 0.3 * scale
        const trunkRadiusBottom = 0.5 * scale
        const trunkHeight = 2 * scale
        const trunkShape = new CANNON.Cylinder(trunkRadiusTop, trunkRadiusBottom, trunkHeight, 8)
        const trunkBody = new CANNON.Body({
            type: CANNON.Body.STATIC,
            shape: trunkShape,
            position: new CANNON.Vec3(x, (trunkHeight / 2), z)
        })
        trunkBody.quaternion.setFromEuler(Math.PI / 2, 0, 0)
        this.worldPhysics.addBody(trunkBody)
    }

    setPhysics() {
        // Main ground
        this.body = new CANNON.Body({
            type: CANNON.Body.STATIC,
            shape: new CANNON.Box(new CANNON.Vec3(100, 0.1, 100))
        })
        this.body.position.y = -0.1
        
        const material = new CANNON.Material('floorMaterial')
        material.friction = 0.5
        material.restitution = 0.1
        this.body.material = material
        
        this.worldPhysics.addBody(this.body)
    }

    addInvisibleWalls() {
        const wallHeight = 8
        const arenaSize = 80
        
        const wallConfigs = [
            { pos: [0, wallHeight/2, -arenaSize], size: [100, wallHeight, 2] },
            { pos: [0, wallHeight/2, arenaSize], size: [100, wallHeight, 2] },
            { pos: [-arenaSize, wallHeight/2, 0], size: [2, wallHeight, 100] },
            { pos: [arenaSize, wallHeight/2, 0], size: [2, wallHeight, 100] }
        ]
        
        const wallMaterial = new CANNON.Material('wallMaterial')
        wallMaterial.friction = 0.1
        wallMaterial.restitution = 0.3
        
        wallConfigs.forEach(config => {
            const wallBody = new CANNON.Body({
                type: CANNON.Body.STATIC,
                shape: new CANNON.Box(new CANNON.Vec3(config.size[0], config.size[1], config.size[2]))
            })
            wallBody.position.set(config.pos[0], config.pos[1], config.pos[2])
            wallBody.material = wallMaterial
            this.worldPhysics.addBody(wallBody)
        })
    }
    
    // Check if car is on a boost pad
    checkBoostPads(carPosition) {
        for (const pad of this.boostPads) {
            const distance = new THREE.Vector2(
                carPosition.x - pad.position.x,
                carPosition.z - pad.position.z
            ).length()
            
            if (distance < pad.radius) {
                return pad
            }
        }
        return null
    }
    
    updateGrassColor(color) {
        this.nintendoColors.grassColor = color
        this.grassMeshes.forEach(mesh => {
            if (mesh.material) {
                mesh.material.color.set(color)
            }
        })
    }
    
    updateFenceColor(color) {
        this.nintendoColors.fenceColor = color
        this.fenceMeshes.forEach(mesh => {
            mesh.traverse(child => {
                if (child.material && child.material.color) {
                    const hex = child.material.color.getHex()
                    if (hex !== 0xFFFFFF) {
                        child.material.color.set(color)
                    }
                }
            })
        })
    }

    /** Aplica cores/textura do mapa ativo (MapCatalog). Cenário GLB opcional. */
    async applyMap(mapRuntime) {
        if (!mapRuntime) return
        if (mapRuntime.grassColor != null && this.updateGrassColor) {
            this.updateGrassColor(mapRuntime.grassColor)
        }
        if (mapRuntime.fenceColor != null && this.updateFenceColor) {
            this.updateFenceColor(mapRuntime.fenceColor)
        }
        // textura de chão
        if (mapRuntime.floorTexture && this.groundMesh?.material) {
            try {
                const mapCatalog = (await import('../Content/MapCatalog.js')).default
                const tex = mapCatalog.loadFloorTexture(mapRuntime.floorTexture)
                if (tex) {
                    this.groundMesh.material.map = tex
                    this.groundMesh.material.needsUpdate = true
                }
            } catch (_) {}
        }
        // cenário
        if (this._sceneryGroup) {
            this.scene.remove(this._sceneryGroup)
            this._sceneryGroup.traverse((o) => {
                o.geometry?.dispose?.()
                const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : []
                mats.forEach((m) => m.dispose?.())
            })
            this._sceneryGroup = null
        }
        if (mapRuntime.scenery) {
            try {
                const mapCatalog = (await import('../Content/MapCatalog.js')).default
                const scene = await mapCatalog.loadScenery(mapRuntime.scenery)
                if (scene) {
                    scene.scale.setScalar(mapRuntime.sceneryScale || 1)
                    scene.position.y = mapRuntime.sceneryY || 0
                    scene.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true } })
                    this._sceneryGroup = scene
                    this.scene.add(scene)
                }
            } catch (e) {
                console.warn('[floor] cenário', e)
            }
        }
        // céu
        if (mapRuntime.skyColor != null && this.experience?.scene) {
            this.experience.scene.background = new THREE.Color(mapRuntime.skyColor)
        }
    }

}
